<template>
  <div :class="['data-table', fillHeight ? 'data-table--fill-height' : '']">
    <div v-if="$slots.toolbar" class="data-table__toolbar">
      <slot name="toolbar" />
    </div>

    <v-data-table
      :headers="headers"
      :items="items"
      :loading="loading"
      :item-value="rowKey"
      :items-per-page="itemsPerPage"
      :page="page"
      :density="vuetifyDensity"
      :class="['data-table__inner', clickable ? 'data-table__inner--clickable' : '']"
      hover
      @click:row="onRowClick"
      @update:page="onPageUpdate"
    >
      <!--
        [수정] 기존에는 $slots 전체를 동적 forward 했으나,
        부모가 정의하지 않은 슬롯이라도 dynamic name expression이 평가되면서
        빈 vnode가 patch될 때 "Cannot read properties of null (reading 'parentNode')"
        가 발생할 여지가 있었음. v-data-table이 실제로 인식하는 표준 슬롯만
        명시적으로 forward 한다.
      -->

      <!-- 표준 위치 슬롯 -->
      <template v-if="$slots.top" #top="props">
        <slot name="top" v-bind="props || {}" />
      </template>
      <template v-if="$slots.bottom" #bottom="props">
        <slot name="bottom" v-bind="props || {}" />
      </template>
      <template v-if="$slots.loading" #loading="props">
        <slot name="loading" v-bind="props || {}" />
      </template>

      <!-- item.<key> — 부모가 정의한 슬롯만 -->
      <template
        v-for="key in itemSlotKeys"
        :key="`item-${key}`"
        #[`item.${key}`]="props"
      >
        <slot :name="`item.${key}`" v-bind="props || {}" />
      </template>

      <!-- header.<key> — 부모가 정의한 슬롯만 -->
      <template
        v-for="key in headerSlotKeys"
        :key="`header-${key}`"
        #[`header.${key}`]="props"
      >
        <slot :name="`header.${key}`" v-bind="props || {}" />
      </template>

      <template #no-data>
        <slot name="empty">
          <EmptyState
            title="데이터가 없습니다"
            description="조건에 해당하는 항목이 없습니다."
          />
        </slot>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import EmptyState from "./EmptyState.vue";

export default {
  name: "DataTable",
  components: { EmptyState },
  props: {
    headers: { type: Array, required: true },
    items: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    density: {
      type: String,
      default: "comfortable",
      validator: (v) => ["compact", "comfortable"].includes(v),
    },
    rowKey: { type: String, default: "id" },
    clickable: { type: Boolean, default: false },
    itemsPerPage: { type: Number, default: 25 },
    // 부모가 높이를 잡아주는 컨테이너(flex 등) 안에서 쓸 때 true로 주면,
    // 툴바/헤더/페이지네이션은 고정되고 행(tbody) 영역만 내부 스크롤된다.
    fillHeight: { type: Boolean, default: false },
    // v-model:page — 부모가 페이지 번호를 저장해뒀다가 복원(예: 상세 페이지에서
    // 뒤로가기 시 보던 페이지 유지)하고 싶을 때 사용. 안 넘기면 항상 1페이지.
    page: { type: Number, default: 1 },
    // 넘기면, 상세 페이지 갔다가 뒤로가기로 돌아왔을 때(= 새 인스턴스로 재마운트)
    // tbody 스크롤 위치도 sessionStorage에 저장했다가 복원한다. 페이지별로 겹치지
    // 않게 고유한 값을 넘겨야 한다(예: 카테고리명).
    scrollKey: { type: String, default: "" },
  },
  emits: ["row-click", "update:page"],
  // [3차] $slots는 reactive하지 않으므로 computed에서 추적하면 트리거가 불안정.
  // 부모 슬롯 정의는 정적이라 created 시점에 한 번만 계산해도 충분.
  data() {
    return {
      itemSlotKeys: [],
      headerSlotKeys: [],
    };
  },
  created() {
    const slotNames = Object.keys(this.$slots || {});
    this.itemSlotKeys = slotNames
      .filter((n) => typeof n === "string" && n.startsWith("item."))
      .map((n) => n.slice("item.".length))
      .filter((k) => k.length > 0);
    this.headerSlotKeys = slotNames
      .filter((n) => typeof n === "string" && n.startsWith("header."))
      .map((n) => n.slice("header.".length))
      .filter((k) => k.length > 0);
  },
  mounted() {
    if (!this.scrollKey) return;
    const wrapper = this.$el.querySelector(".v-table__wrapper");
    if (!wrapper) return;
    this._scrollWrapper = wrapper;
    this._onTableScroll = () => {
      sessionStorage.setItem(this.scrollStorageKey, String(wrapper.scrollTop));
    };
    wrapper.addEventListener("scroll", this._onTableScroll);
    // 데이터가 이미 준비된 채로 마운트되는 경우(예: 다른 탭에서 이미 fetch된
    // 목록을 재사용)도 있으니, 마운트 시점에도 한 번 복원 시도.
    if (this.items.length > 0) this.restoreScroll();
  },
  beforeUnmount() {
    if (this._scrollWrapper && this._onTableScroll) {
      this._scrollWrapper.removeEventListener("scroll", this._onTableScroll);
    }
  },
  computed: {
    vuetifyDensity() {
      // gaide §5.1: comfortable=48px, compact=40px
      return this.density === "compact" ? "compact" : "default";
    },
    scrollStorageKey() {
      return `dataTableScroll:${this.scrollKey}`;
    },
  },
  watch: {
    // 목록이 비어있다가 처음 채워지는 시점(비동기 fetch 완료) = 복원을 시도할 때.
    items(newItems, oldItems) {
      if (!this.scrollKey) return;
      if ((!oldItems || oldItems.length === 0) && newItems && newItems.length > 0) {
        this.restoreScroll();
      }
    },
    // scrollKey 자체가 바뀌는 경우(같은 인스턴스를 유지한 채 다른 탭/카테고리로
    // 전환) — 이때는 예전 스크롤을 복원하지 않고 항상 맨 위부터 보여준다.
    // (상세 페이지 갔다가 뒤로가기로 돌아와 재마운트되는 경우만 복원 대상이며,
    // 그건 위 items watch가 처리한다.)
    scrollKey() {
      this.$nextTick(() => {
        const wrapper = this._scrollWrapper || this.$el.querySelector(".v-table__wrapper");
        if (wrapper) wrapper.scrollTop = 0;
      });
    },
  },
  methods: {
    onRowClick(_event, { item }) {
      if (this.clickable) this.$emit("row-click", item);
    },
    onPageUpdate(newPage) {
      this.$emit("update:page", newPage);
      // 페이지가 바뀌면(사용자가 페이지네이션을 조작한 경우) 이전 페이지에서
      // 스크롤해둔 위치가 아니라 새 페이지 맨 위부터 보이게 한다.
      this.$nextTick(() => {
        const wrapper = this._scrollWrapper || this.$el.querySelector(".v-table__wrapper");
        if (wrapper) wrapper.scrollTop = 0;
      });
    },
    restoreScroll() {
      this.$nextTick(() => {
        const wrapper = this._scrollWrapper || this.$el.querySelector(".v-table__wrapper");
        if (!wrapper) return;
        const saved = parseInt(sessionStorage.getItem(this.scrollStorageKey), 10);
        if (Number.isFinite(saved)) wrapper.scrollTop = saved;
      });
    },
  },
};
</script>

<style scoped>
.data-table {
  background: var(--bg-surface);
  border: 1px solid var(--border-default);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-sm);
  overflow: hidden;
}

.data-table__toolbar {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-3) var(--space-4);
  border-bottom: 1px solid var(--border-default);
  background: var(--bg-surface);
}

/* fillHeight — 부모(flex)가 준 높이를 그대로 채우고, v-data-table 내부의
   thead/tfoot(페이지네이션)은 고정, tbody 스크롤 영역(.v-table__wrapper)만
   내부 스크롤되도록 한다. v-table/.v-table__wrapper는 Vuetify 기본값이
   이미 flex-column + flex:1 1 auto라 v-data-table 자체 높이만 잡아주면 된다. */
.data-table--fill-height {
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
}
.data-table--fill-height .data-table__toolbar {
  flex: 0 0 auto;
}
.data-table--fill-height :deep(.v-data-table) {
  flex: 1 1 auto;
  min-height: 0;
}

.data-table__inner {
  background: var(--bg-surface) !important;
}

.data-table__inner :deep(thead th) {
  background: var(--bg-subtle) !important;
  color: var(--text-secondary) !important;
  font-size: var(--font-body-strong-size) !important;
  font-weight: var(--font-body-strong-weight) !important;
  line-height: var(--font-body-strong-line) !important;
  white-space: nowrap;
  position: sticky;
  top: 0;
  z-index: 1;
  border-bottom: 1px solid var(--border-default) !important;
}

.data-table__inner :deep(tbody td) {
  font-size: var(--font-body-size);
  line-height: var(--font-body-line);
  color: var(--text-primary);
  border-bottom: 1px solid var(--border-default) !important;
  background: var(--bg-surface);
}

/* 정렬 가능한 헤더는 정렬 아이콘이 opacity:0으로 숨겨져 있어도 자리를 차지해
   헤더 텍스트가 실제 중앙(또는 start 정렬 기준점)보다 안쪽으로 쏠려 보이고,
   그 아래 값과 비교하면 어긋나 보인다. 정렬 중이 아닌 컬럼은 아이콘을 완전히
   제거해 헤더 텍스트가 진짜 정렬 기준 위치에 오도록 한다. (DataTable을 쓰는
   모든 테이블에 공통 적용) */
.data-table__inner :deep(.v-data-table-header__sort-icon) {
  display: none;
}
.data-table__inner :deep(.v-data-table__th--sorted .v-data-table-header__sort-icon) {
  display: inline-flex;
}

.data-table__inner :deep(tbody tr:hover td) {
  background: var(--color-primary-50) !important;
}

.data-table__inner--clickable :deep(tbody tr) {
  cursor: pointer;
}

/* Vuetify zebra/striped 비활성화 */
.data-table__inner :deep(tbody tr.v-data-table__tr--striped td) {
  background: var(--bg-surface) !important;
}
</style>
